import React, { useEffect, useMemo, useState } from 'react'
import { Alert, notification, Modal } from 'antd'
import { RouteComponentProps } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { ApplicationState } from 'src/store'
import { ButtonTitled, PageHeader } from 'src/components/Ant'
import {
  getConferences,
  setConferenceStatus,
  SetConferenceStatusProps
} from 'src/core/store/ducks/conferences/actions'
import { getFullRequest } from 'src/core/store/ducks/requests/actions'
import { Request, RequestConferenceStatus, RequestUserData } from 'src/types'
import SegmentControl, { Segments } from 'src/components/SegmentControl'
import useReasonPolicyRule from 'src/core/hooks/use-reason-policy-rule'

import * as CSS from './styles'

import { shouldShowRDVButton } from '../utils'
import ModalAction, { ModalActionType } from '../../components/modal-action'
import RDVButton from '../../components/RDVButton'
import Receipts from '../../components/Receipts'
import RequestSummary from '../../components/RequestSummary'
import { SaveObservationRequest } from 'src/services/api/requests'
import { IconEditRequestTitle } from 'src/components/atoms/edit-request-title'

interface Params {
  request_id?: string
}

type SegmentKeys = 'receipts' | 'summary'

type SummarySegments = Segments<SegmentKeys>

export default function ConferencesSummary({
  history,
  match: {
    params: { request_id }
  }
}: RouteComponentProps<Params>) {
  const {
    conferences,
    me: {
      user: { auditor, role_group, user_level_type }
    },
    requests
  } = useSelector((state: ApplicationState) => ({
    conferences: state.conferences,
    me: state.me,
    requests: state.requests
  }))
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [action, setAction] = useState<ModalActionType | null>(null)
  const [changingStatus, setChangingStatus] = useState<boolean>(false)
  const [current, setCurrent] = useState<SegmentKeys>('summary')
  const [savingFullRequest, setSavingFullRequest] = useState<boolean>(false)

  const policyRule = useReasonPolicyRule(
    requests.fullRequest?.request_user_data[0].reason_id
  )

  const isSuperAdmin = ['SUPER_ADMIN'].includes(user_level_type)

  const enableCancelAction = [
    'CONFERENCE_PENDING',
    'CONFERENCE_REPROVED'
  ].includes(requests.fullRequest?.status)
  const enableApprovalAction = ['CONFERENCE_PENDING', 'UNDERWAY'].includes(
    requests.fullRequest?.status
  )
  const enableReproveAction =
    requests.fullRequest?.status === 'CONFERENCE_PENDING'

  const enableReceiptCheck = policyRule?.enable_check_receipt || false

  async function saveRequest(request: Request) {
    Modal.confirm({
      cancelText: t('common.actions.cancel'),
      content: t('web.summary.change-observation-confirm'),
      onCancel() {},
      onOk: async () => {
        await SaveObservationRequest({
          organization_id: request.organization_id,
          request_id: request.request_id,
          obs: request.obs
        })
        setTimeout(() => {
          dispatch(getFullRequest(request.request_id, 'conferences'))
        }, 100)
      },
      title: t('common.actions.delete.title')
    })
    return await new Promise(() => {})
  }

  const segments = useMemo<SummarySegments>(
    () => ({
      summary: {
        disabled: false,
        title: 'web.summary.title',
        renderContent: () => (
          <RequestSummary
            enableAdvancesEdit={enableApprovalAction}
            enableBillingEdit={enableApprovalAction}
            enableBudget={policyRule?.enable_budget}
            enableItinerariesEdit={enableApprovalAction}
            enableSharingEdit={
              isSuperAdmin ? isSuperAdmin : enableApprovalAction
            }
            originRequest="conferences"
            request={requests.fullRequest}
            saveRequest={saveRequest}
            enableEditObservation={
              requests.fullRequest.status === 'CONFERENCE_PENDING'
            }
          />
        )
      },
      receipts: {
        disabled: false,
        title: 'web.receipts.title',
        renderContent: () => (
          <Receipts
            conferences
            enableReceiptCheck={enableReceiptCheck}
            request={requests.fullRequest}
          />
        )
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enableApprovalAction, enableReceiptCheck, policyRule, requests.fullRequest]
  )

  useEffect(() => {
    dispatch(getConferences({ page: 1, request_ids: [request_id] }))
    dispatch(getFullRequest(request_id, 'conferences'))
  }, [dispatch, request_id])

  useEffect(() => {
    if (conferences.setStatusLoading !== changingStatus) {
      setChangingStatus(conferences.setStatusLoading)

      if (!conferences.setStatusLoading) {
        if (conferences.setStatusError) {
          notification.error({
            duration: 10,
            message: conferences.setStatusError,
            placement: 'bottomRight'
          })
        } else {
          notification.success({
            duration: 5,
            message: conferences.setStatusSuccess,
            placement: 'bottomRight'
          })
          history.push('/conferences')
        }

        Modal.destroyAll()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conferences.setStatusLoading])

  useEffect(() => {
    if (requests.saveFullRequestLoading !== savingFullRequest) {
      setSavingFullRequest(requests.saveFullRequestLoading)

      if (!requests.saveFullRequestLoading) {
        if (requests.saveFullRequestError) {
          notification.error({
            duration: 5,
            message: requests.saveFullRequestError,
            placement: 'bottomRight'
          })
        } else {
          notification.success({
            duration: 7,
            message: requests.saveFullRequestSuccess,
            placement: 'bottomRight'
          })
        }

        Modal.destroyAll()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests.saveFullRequestLoading])

  function toggleAction(action: 'approve' | 'cancel' | 'disapprove' | null) {
    if (!action) {
      setAction(null)
      return
    }

    let actionTitle = ''
    switch (action) {
      case 'approve':
        actionTitle = t('web.conferences.modal-accept')
        break
      case 'disapprove':
        actionTitle = t('web.conferences.modal-decline')
        break
      case 'cancel':
        actionTitle = t('web.conferences.modal-cancel')
    }

    setAction({
      action,
      title: actionTitle
    })
  }

  function userReduce(acc: RequestUserData[], cur: RequestUserData) {
    const canApprove =
      (cur.status === 'CONFERENCE_PENDING' && cur.conference) ||
      (cur.status === 'CONFERENCE_REPROVED' && cur.allowed)

    if (!canApprove) {
      return acc
    }

    const user_data = cur.request_conferences.filter(
      conference => conference.status === 'PENDING'
    )
    return [...acc, { ...cur, request_conferences: user_data }]
  }

  function handleSubmit(justification: string, users: RequestUserData[]) {
    let status: RequestConferenceStatus
    switch (action.action) {
      case 'approve':
        status = 'APPROVED'
        break
      case 'cancel':
        status = 'CANCELED'
        break
      case 'disapprove':
        status = 'REPROVED'
        break
    }

    const conferenceStatus: SetConferenceStatusProps = {
      justification,
      request_conference_ids: users.reduce(
        (acc, { request_conferences }) => [
          ...acc,
          ...request_conferences
            .filter(
              ({ situation, status: request_conference_status }) =>
                action.action === 'cancel' ||
                (situation === 'ACTIVE' &&
                  request_conference_status === 'PENDING')
            )
            .map(({ request_conference_id }) => request_conference_id)
        ],
        []
      ),
      request_id,
      status
    }

    if (enableReceiptCheck) {
      conferenceStatus.checked_receipts =
        requests.fullRequest.request_tickets.map(
          ({ request_ticket_id, request_ticket_receipts }) => ({
            request_ticket_id: +request_ticket_id,
            check_receipt: request_ticket_receipts.some(
              ({ checked_receipt }) => checked_receipt
            )
          })
        )
    }
    dispatch(setConferenceStatus(conferenceStatus))
  }

  if (requests.fullRequestLoading || conferences.loading) {
    return <CSS.Spin />
  }

  const hasError = requests.fullRequestError || conferences.error
  if (hasError) {
    return <Alert message={hasError} showIcon type="error" />
  }

  if (!requests.fullRequest) {
    const message = t('common.response.unexpected-error')
    return <Alert message={message} showIcon type="error" />
  }

  return (
    <>
      <PageHeader
        extra={[
          enableCancelAction && (
            <ButtonTitled
              data-test-id="summary-conference-cancel"
              icon="delete"
              key="btnCancel"
              onClick={() => toggleAction('cancel')}
              size="default"
              tooltip="web.conferences.cancel"
              type="default"
            />
          ),
          enableReproveAction && (
            <ButtonTitled
              data-test-id="summary-conference-disapprove"
              icon="close"
              key="btnDisapprove"
              onClick={() => toggleAction('disapprove')}
              size="default"
              tooltip="web.conferences.decline"
              type="danger"
            />
          ),
          enableApprovalAction && (
            <ButtonTitled
              data-test-id="summary-conference-submit"
              icon="check"
              key="btnApprove"
              onClick={() => toggleAction('approve')}
              size="default"
              tooltip="web.conferences.accept"
            />
          ),
          shouldShowRDVButton({
            request: requests.fullRequest,
            roleGroup: role_group
          }) && <RDVButton key="btnRDV" request={requests.fullRequest} />
        ]}
        onBack={() => history.goBack()}
        subTitle={
          <>
            {`#${requests.fullRequest.request_number} - ${requests.fullRequest.title} `}
            {auditor && (
              <IconEditRequestTitle
                organizationId={requests.fullRequest.organization_id}
                requestId={requests.fullRequest.request_id}
                title={requests.fullRequest.title}
                update={() =>
                  dispatch(getFullRequest(request_id, 'conferences'))
                }
              />
            )}
          </>
        }
        title="web.conferences.title"
      />
      <CSS.SegmentControl>
        <SegmentControl
          defaultValue="summary"
          onChange={({ target: { value } }) => {
            setCurrent(value)
          }}
          segments={segments}
          value={current}
        />
      </CSS.SegmentControl>
      {segments[current].renderContent()}
      <ModalAction
        handleSubmit={handleSubmit}
        isLoading={
          conferences.setStatusLoading ||
          requests.fullRequestLoading ||
          conferences.loading
        }
        modalAction={action}
        request_user_data={requests.fullRequest?.request_user_data}
        toggleAction={toggleAction}
        userReduce={userReduce}
      />
    </>
  )
}
